import React, { useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useNavigate } from 'react-router-dom';
import './App.css';
import { Banner, Brand, Video, Country, Product, AddProduct, EditProduct, SupportChat, SupportTicket, GeneralSetting, BankDetails, AddCoupon } from './screens';
import Coupons from './screens/Coupon/Coupons';
import UserList from './screens/Chat/UserList';
import AllUserList from './screens/Chat/AllUserList';
import Chat from './screens/Chat/Chat';
import Categories from './screens/Categories/Categories';
import AddCategory from './screens/AddCategory/AddCategory';
import EditCategory from './screens/EditCategory/EditCategory';
import Dashboard from './screens/Dashboard/Dashboard';
import Layout from './screens/Layout/Layout';
import Login from './screens/Login/Login';
import Notification from './screens/Notification/Notification';
import RetailUsers from './screens/RetailUsers/RetailUsers';
import SalesPerson from './screens/SalesPerson/SalesPerson';
import WholesaleUsers from './screens/WholesaleUsers/WholesaleUsers';
import Order from './screens/Order/Order';
import OrderDetails from './screens/OrderDetails/OrderDetails';
import ProductDetails from './screens/ProductDetails/ProductDetails';
import { Provider } from 'react-redux';
import store from './store';
import Report from './screens/Report/Report';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { requestForToken, isFirebaseMessagingSupported } from './utils/firebase';
import { setGlobalNavigate } from './components/notificationToast/NotificationToast';
import ProductRatingReview from './screens/ProductRatingReview/ProductRatingReview';
import ProductImageUpload from './screens/ProductImageUpload/ProductImageUpload';

// Component to set up global navigation
const NavigationSetup = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setGlobalNavigate(navigate);
  }, [navigate]);

  return null;
};

function App() {

  useEffect(() => {
    const initializeNotifications = async () => {
      // Check if Firebase messaging is supported
      const isSupported = isFirebaseMessagingSupported();

      if (!isSupported) {
        console.log("Firebase messaging is not supported in this browser");
        return;
      }

      // Check if browser supports notifications
      if ('Notification' in window) {
        try {
          const permission = await window.Notification.requestPermission();
          if (permission === "granted") {
            console.log("Notification permission granted");
            // Request Firebase token
            const token = await requestForToken();
            if (token) {
              console.log("Firebase token obtained successfully");
            }
          } else {
            console.log("Notification permission not granted");
          }
        } catch (error) {
          console.error("Error requesting notification permission:", error);
        }
      } else {
        console.log("This browser does not support notifications");
      }
    };

    // Add a small delay to ensure Firebase is initialized
    setTimeout(initializeNotifications, 1000);
  }, []);

  return (
    <Provider store={store}>
      <Router>
        <NavigationSetup />
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={
              <Layout>
                <Dashboard />
              </Layout>
            } />
            <Route path="/salesperson" element={
              <Layout>
                <SalesPerson />
              </Layout>
            } />
            <Route path="/coupons" element={
              <Layout>
                <Coupons />
              </Layout>
            } />
            <Route path="/add-coupon" element={
              <Layout>
                <AddCoupon />
              </Layout>
            } />
            <Route path="/wholesaleuser" element={
              <Layout>
                <WholesaleUsers />
              </Layout>
            } />
            <Route path="/retailusers" element={
              <Layout>
                <RetailUsers />
              </Layout>
            } />
            <Route path="/order" element={
              <Layout>
                <Order />
              </Layout>
            } />
            <Route path="/order-details/:orderId" element={
              <Layout>
                <OrderDetails />
              </Layout>
            } />
            <Route path="/categories" element={
              <Layout>
                <Categories />
              </Layout>
            } />
            <Route path="/add-category/:catId?" element={
              <Layout>
                <AddCategory />
              </Layout>
            } />
            <Route path="/edit-category/:id/:catId?" element={
              <Layout>
                <EditCategory />
              </Layout>
            } />
            <Route path="/product" element={
              <Layout>
                <Product />
              </Layout>
            } />
            <Route path="/add-product/:catId?" element={
              <Layout>
                <AddProduct />
              </Layout>
            } />
            <Route path="/edit-product/:id/:catId?" element={
              <Layout>
                <EditProduct />
              </Layout>
            } />
            <Route path="/brand" element={
              <Layout>
                <Brand />
              </Layout>
            } />
            <Route path="/banner" element={
              <Layout>
                <Banner />
              </Layout>
            } />
            <Route path="/video" element={
              <Layout>
                <Video />
              </Layout>
            } />
            <Route path="/country" element={
              <Layout>
                <Country />
              </Layout>
            } />
            {/* <Route path="/state" element={
            <Layout>
              <State />
            </Layout>
          } />
          <Route path="/city" element={ 
            <Layout>
              <City/>
            </Layout>
          } />
          <Route path="/pincode" element={
            <Layout>
              <Pincode />
            </Layout>
          } /> */}
            <Route path="/supportticket" element={
              <Layout>
                <SupportTicket />
              </Layout>
            } />
            <Route path="/product-details/:id" element={
              <Layout>
                <ProductDetails />
              </Layout>
            } />
            <Route path="/support-chat" element={
              <Layout>
                <SupportChat />
              </Layout>
            } />
            <Route path="/generalsetting" element={
              <Layout>
                <GeneralSetting />
              </Layout>
            } />
            <Route path="/bankdetails" element={
              <Layout>
                <BankDetails />
              </Layout>
            } />
            <Route path="/report" element={
              <Layout>
                <Report />
              </Layout>
            } />
            <Route path="/notifications" element={
              <Layout>
                <Notification />
              </Layout>
            } />
            <Route path="/chat" element={
              <Layout>
                <UserList />
              </Layout>
            } />
            <Route path="/all-users" element={
              <Layout>
                <AllUserList />
              </Layout>
            } />
            <Route path="/chat-conversation" element={
              <Layout>
                <Chat />
              </Layout>
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/product-rating-review/:id" element={
              <Layout>
                <ProductRatingReview />
              </Layout>
            } />
            <Route path="/product-image-upload/:productId"  element={
              <Layout>
                <ProductImageUpload />
              </Layout>
            } />
          </Routes>
        </div>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Provider>
  );
}

export default App;
